import { createSlice } from "@reduxjs/toolkit";
import {
  createErrorReportingAsyncThunk,
  serverRejectionMessage,
  ServerRejection,
} from "../../helpers";
import type { Dictionary } from "lodash";
import axios, { getData, isAxiosError } from "../../../axios";
import {
  PostRental,
  Rental,
  UpdateRental,
  decodeRental,
  decodeRentals,
} from "./types";
import keyBy from "lodash/keyBy";
import { notifyL } from "../../notifications";

const prefix = "data/rentals";

type State = {
  entities: Dictionary<Rental>;
  ids: string[];
  loading: boolean;
};

const initialState: State = { entities: {}, ids: [], loading: false };

export const loadRentals = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (): Promise<Rental[]> => {
    return await axios.get("/api/rentals").then(getData).then(decodeRentals);
  }
);

// One rental by id, for callers that hold a `rentalId` and need the rental
// itself - a cargo-equipment link, for instance, carries the id but not the
// number a dispatcher recognises.
//
// 404 is the only tolerated failure, and it answers null: a rental can be
// deleted after its id was written onto something else, and a rental that is
// no longer there is nothing to report or to interrupt the user with. Every
// other outcome - 403 (an id pointing at another org's rental), 5xx, a body
// that no longer decodes - is a real fault and takes the thunk's normal
// Sentry/log/toast path.
export const loadRental = createErrorReportingAsyncThunk(
  `${prefix}/load-one`,
  async (id: string): Promise<Rental | null> => {
    try {
      return await axios
        .get(`/api/rentals/${id}`)
        .then(getData)
        .then(decodeRental);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  }
);

// The server validates the rental (customer, dates, equipment availability)
// and answers 400 { message } with a reason meant for the user. Surface that
// refusal as the rejection payload so the dialog can decide what to show;
// anything else is an unexpected failure and goes through the normal
// reporting/toast path.
export const createRental = createErrorReportingAsyncThunk<
  Rental,
  PostRental,
  { rejectValue: ServerRejection }
>(`${prefix}/create`, async (input, thunkApi) => {
  try {
    const rental = await axios
      .post("/api/rentals", input)
      .then(getData)
      .then(decodeRental);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "rentalSaved",
        type: "success",
      })
    );
    return rental;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (rejection) {
      return thunkApi.rejectWithValue(rejection);
    }
    throw err;
  }
});

// Same server contract as create: 400 { message } when the rental cannot be
// edited (cancelled / already invoiced) or fails validation.
export const updateRental = createErrorReportingAsyncThunk<
  Rental,
  { id: string; input: UpdateRental },
  { rejectValue: ServerRejection }
>(`${prefix}/update`, async ({ id, input }, thunkApi) => {
  try {
    const rental = await axios
      .put(`/api/rentals/${id}`, input)
      .then(getData)
      .then(decodeRental);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "rentalSaved",
        type: "success",
      })
    );
    return rental;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (rejection) {
      return thunkApi.rejectWithValue(rejection);
    }
    throw err;
  }
});

export const cancelRental = createErrorReportingAsyncThunk(
  `${prefix}/cancel`,
  async (id: string): Promise<Rental> =>
    axios.post(`/api/rentals/${id}/cancel`).then(getData).then(decodeRental)
);

// `date` is the day it happened, for a hand-over registered afterwards. Null
// is today.
export const deliverRental = createErrorReportingAsyncThunk(
  `${prefix}/deliver`,
  async (input: { id: string; date: string | null }): Promise<Rental> =>
    axios
      .post(`/api/rentals/${input.id}/deliver`, { date: input.date })
      .then(getData)
      .then(decodeRental)
);

export const returnRental = createErrorReportingAsyncThunk(
  `${prefix}/return`,
  async (input: { id: string; date: string | null }): Promise<Rental> =>
    axios
      .post(`/api/rentals/${input.id}/return`, { date: input.date })
      .then(getData)
      .then(decodeRental)
);

export const markRentalInvoiced = createErrorReportingAsyncThunk(
  `${prefix}/mark-invoiced`,
  async (id: string): Promise<Rental> =>
    axios
      .post(`/api/rentals/${id}/mark-invoiced`)
      .then(getData)
      .then(decodeRental)
);

const upsert = (state: State, rental: Rental) => {
  if (!state.entities[rental.id]) {
    state.ids.unshift(rental.id);
  }
  state.entities[rental.id] = rental;
};

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadRentals.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadRentals.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = keyBy(action.payload, "id");
        state.ids = action.payload.map((x) => x.id);
      })
      .addCase(loadRentals.rejected, (state) => {
        state.loading = false;
      })
      // The entity only: a lookup by id must not change which rentals the
      // list shows.
      .addCase(loadRental.fulfilled, (state, action) => {
        if (action.payload) state.entities[action.payload.id] = action.payload;
      })
      .addCase(createRental.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(updateRental.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(cancelRental.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(deliverRental.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(returnRental.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(markRentalInvoiced.fulfilled, (state, action) => {
        upsert(state, action.payload);
      });
  },
});

export default slice.reducer;
